import { c as _c } from "react/compiler-runtime";
import type { ProductCard, ProductOption, ProductVariant, ShopifyImage as ShopifyProductImage } from '@repo/shopify';
import { Star } from 'lucide-react';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { formatMoney } from '@/lib/money';
import { getProductCardPricing } from '@/lib/product-card-pricing';
import { ProductBadgeChip, ProductBadgeStack, resolveProductBadges, salePercentOff } from './product-badges';
const SIZE_OPTION_NAMES = new Set(['size', 'waist', 'length', 'width']);
export interface ProductCardProps {
  product: ProductCard;
  showQuickAdd?: boolean;
  showVendor?: boolean;
  imageSizes?: string;
  preloadPrimaryImage?: boolean;
  enableReviews?: boolean;
  moneyLocale?: string;
  /** Average rating (e.g. 4.8). Wire to Klaviyo Reviews API. */
  rating?: number | null;
  /** Review count for display. Wire to Klaviyo Reviews API. */
  reviewCount?: number | null;
}
interface ProductCardFrameProps extends ProductCardProps {
  quickAddSlot?: ReactNode;
}
export interface ProductCardQuickAddData {
  primaryImage: ShopifyProductImage | null;
  defaultVariant: ProductVariant | null;
  hasMultipleVariants: boolean;
  hasSizeOption: boolean;
}
function getSizeOption(options: ProductOption[]) {
  return options.find(option => SIZE_OPTION_NAMES.has(option.name.toLowerCase()));
}
export function getProductCardQuickAddData(product: ProductCard): ProductCardQuickAddData {
  const variants = product.variants?.edges?.map(edge => edge.node) || [];
  const options = product.options || [];
  return {
    primaryImage: product.images?.edges?.[0]?.node as ShopifyProductImage | undefined || product.featuredImage,
    defaultVariant: variants.find(variant => variant.availableForSale) || variants[0] || null,
    hasMultipleVariants: options.length > 0 && !(options.length === 1 && options[0]?.values.length === 1),
    hasSizeOption: Boolean(getSizeOption(options))
  };
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: product cards intentionally combine image, merchandising badges, ratings, and price presentation in one render path
export function ProductCardFrame(t0) {
  const $ = _c(90);
  const {
    product,
    showVendor: t1,
    imageSizes: t2,
    preloadPrimaryImage: t3,
    enableReviews: t4,
    moneyLocale,
    rating: t5,
    reviewCount: t6,
    quickAddSlot
  } = t0;
  const showVendor = t1 === undefined ? false : t1;
  const imageSizes = t2 === undefined ? "(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw" : t2;
  const preloadPrimaryImage = t3 === undefined ? false : t3;
  const enableReviews = t4 === undefined ? false : t4;
  const ratingProp = t5 === undefined ? null : t5;
  const reviewCountProp = t6 === undefined ? null : t6;
  let t7;
  if ($[0] !== product.images?.edges) {
    t7 = product.images?.edges?.map(_temp) || [];
    $[0] = product.images?.edges;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const images = t7;
  const primaryImage = images[0] || product.featuredImage;
  const secondaryImage = images[1];
  const oneTimePrice = product.priceRange.minVariantPrice;
  const oneTimeCompareAtPrice = product.compareAtPriceRange?.minVariantPrice;
  const isOnSale = Boolean(oneTimeCompareAtPrice && Number.parseFloat(oneTimeCompareAtPrice.amount) > Number.parseFloat(oneTimePrice.amount));
  let isSubscriptionPrice;
  let pricing;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t8;
  let t9;
  if ($[2] !== enableReviews || $[3] !== imageSizes || $[4] !== isOnSale || $[5] !== moneyLocale || $[6] !== oneTimeCompareAtPrice?.amount || $[7] !== oneTimePrice.amount || $[8] !== preloadPrimaryImage || $[9] !== primaryImage || $[10] !== product || $[11] !== quickAddSlot || $[12] !== ratingProp || $[13] !== reviewCountProp || $[14] !== secondaryImage || $[15] !== showVendor) {
    pricing = getProductCardPricing(product);
    const hasDiscount = pricing.compareAtPrice !== null;
    isSubscriptionPrice = pricing.kind === "subscription";
    const productUrl = `/products/${product.handle}`;
    let t17;
    if ($[27] !== isOnSale || $[28] !== oneTimeCompareAtPrice?.amount || $[29] !== oneTimePrice.amount || $[30] !== product.availableForSale || $[31] !== product.tags) {
      t17 = resolveProductBadges({
        tags: product.tags,
        availableForSale: product.availableForSale,
        isOnSale,
        percentOff: salePercentOff(oneTimePrice.amount, oneTimeCompareAtPrice?.amount)
      });
      $[27] = isOnSale;
      $[28] = oneTimeCompareAtPrice?.amount;
      $[29] = oneTimePrice.amount;
      $[30] = product.availableForSale;
      $[31] = product.tags;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    const {
      imageBadges,
      titleBadge
    } = t17;
    const rating = enableReviews ? ratingProp ?? null : null;
    const reviewCount = enableReviews ? reviewCountProp ?? null : null;
    const showReviewBadge = rating != null && rating > 0 && reviewCount != null && reviewCount > 0;
    let t18;
    if ($[33] !== rating || $[34] !== showReviewBadge) {
      t18 = showReviewBadge ? rating.toFixed(1) : null;
      $[33] = rating;
      $[34] = showReviewBadge;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    const ratingLabel = t18;
    const hasSecondaryImage = Boolean(secondaryImage);
    t15 = "group relative";
    let t19;
    if ($[36] !== hasSecondaryImage || $[37] !== imageSizes || $[38] !== preloadPrimaryImage || $[39] !== primaryImage || $[40] !== product.title) {
      t19 = primaryImage && <ShopifyImage src={primaryImage.url} alt={primaryImage.altText || product.title} fill={true} preload={preloadPrimaryImage} loading={preloadPrimaryImage ? "eager" : "lazy"} fetchPriority={preloadPrimaryImage ? "high" : undefined} decoding={preloadPrimaryImage ? "sync" : undefined} disableBlur={preloadPrimaryImage} className={`object-cover transition-opacity duration-300 ${hasSecondaryImage ? "md:group-hover:opacity-0" : ""}`} sizes={imageSizes} />;
      $[36] = hasSecondaryImage;
      $[37] = imageSizes;
      $[38] = preloadPrimaryImage;
      $[39] = primaryImage;
      $[40] = product.title;
      $[41] = t19;
    } else {
      t19 = $[41];
    }
    let t20;
    if ($[42] !== imageSizes || $[43] !== product.title || $[44] !== secondaryImage) {
      t20 = secondaryImage && <ShopifyImage src={secondaryImage.url} alt={secondaryImage.altText || product.title} fill={true} loading="lazy" className="hidden object-cover opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" sizes={imageSizes} />;
      $[42] = imageSizes;
      $[43] = product.title;
      $[44] = secondaryImage;
      $[45] = t20;
    } else {
      t20 = $[45];
    }
    let t21;
    if ($[46] !== imageBadges) {
      t21 = <ProductBadgeStack badges={imageBadges} className="absolute left-3 top-3 flex flex-col items-start gap-1.5" />;
      $[46] = imageBadges;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    let t22;
    if ($[48] !== quickAddSlot || $[49] !== t19 || $[50] !== t20 || $[51] !== t21) {
      t22 = <div className="relative aspect-[3/4] overflow-hidden rounded bg-neutral-100">{t19}{t20}{t21}{quickAddSlot}</div>;
      $[48] = quickAddSlot;
      $[49] = t19;
      $[50] = t20;
      $[51] = t21;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    if ($[53] !== productUrl || $[54] !== t22) {
      t16 = <Link href={productUrl} className="block">{t22}</Link>;
      $[53] = productUrl;
      $[54] = t22;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    t11 = "mt-3";
    if ($[56] !== product.vendor || $[57] !== showVendor) {
      t12 = showVendor && product.vendor && <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">{product.vendor}</p>;
      $[56] = product.vendor;
      $[57] = showVendor;
      $[58] = t12;
    } else {
      t12 = $[58];
    }
    let t23;
    if ($[59] !== product.title) {
      t23 = <h3 className="text-[13px] font-semibold uppercase text-neutral-900 group-hover:underline">{product.title}</h3>;
      $[59] = product.title;
      $[60] = t23;
    } else {
      t23 = $[60];
    }
    let t24;
    if ($[61] !== productUrl || $[62] !== t23) {
      t24 = <Link href={productUrl} className="min-w-0 flex-1">{t23}</Link>;
      $[61] = productUrl;
      $[62] = t23;
      $[63] = t24;
    } else {
      t24 = $[63];
    }
    let t25;
    if ($[64] !== ratingLabel || $[65] !== showReviewBadge) {
      t25 = showReviewBadge && ratingLabel && <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-neutral-900"><Star className="h-3 w-3 fill-neutral-900 text-neutral-900" aria-hidden="true" /><span>{ratingLabel}</span></span>;
      $[64] = ratingLabel;
      $[65] = showReviewBadge;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    if ($[67] !== t24 || $[68] !== t25) {
      t13 = <div className="mt-1 flex items-start justify-between gap-3">{t24}{t25}</div>;
      $[67] = t24;
      $[68] = t25;
      $[69] = t13;
    } else {
      t13 = $[69];
    }
    if ($[70] !== titleBadge) {
      t14 = titleBadge && <div className="mt-2"><ProductBadgeChip badge={titleBadge} /></div>;
      $[70] = titleBadge;
      $[71] = t14;
    } else {
      t14 = $[71];
    }
    t8 = "mt-1 flex flex-wrap items-center gap-x-2 gap-y-1";
    t9 = <span className={`text-[13px] font-semibold ${hasDiscount ? "text-brand-accent" : "text-neutral-900"}`}>{formatMoney(pricing.price, moneyLocale)}</span>;
    t10 = pricing.compareAtPrice && <span className="text-[13px] text-neutral-500 line-through">{formatMoney(pricing.compareAtPrice, moneyLocale)}</span>;
    $[2] = enableReviews;
    $[3] = imageSizes;
    $[4] = isOnSale;
    $[5] = moneyLocale;
    $[6] = oneTimeCompareAtPrice?.amount;
    $[7] = oneTimePrice.amount;
    $[8] = preloadPrimaryImage;
    $[9] = primaryImage;
    $[10] = product;
    $[11] = quickAddSlot;
    $[12] = ratingProp;
    $[13] = reviewCountProp;
    $[14] = secondaryImage;
    $[15] = showVendor;
    $[16] = isSubscriptionPrice;
    $[17] = pricing;
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t8;
    $[26] = t9;
  } else {
    isSubscriptionPrice = $[16];
    pricing = $[17];
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
    t13 = $[21];
    t14 = $[22];
    t15 = $[23];
    t16 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t17;
  if ($[72] !== isSubscriptionPrice || $[73] !== pricing) {
    t17 = isSubscriptionPrice && pricing.discountPercent !== null && <span className="rounded bg-brand-accent px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-white">{pricing.discountPercent}% off</span>;
    $[72] = isSubscriptionPrice;
    $[73] = pricing;
    $[74] = t17;
  } else {
    t17 = $[74];
  }
  let t18;
  if ($[75] !== t10 || $[76] !== t17 || $[77] !== t8 || $[78] !== t9) {
    t18 = <div className={t8}>{t9}{t10}{t17}</div>;
    $[75] = t10;
    $[76] = t17;
    $[77] = t8;
    $[78] = t9;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] !== t11 || $[81] !== t12 || $[82] !== t13 || $[83] !== t14 || $[84] !== t18) {
    t19 = <div className={t11}>{t12}{t13}{t14}{t18}</div>;
    $[80] = t11;
    $[81] = t12;
    $[82] = t13;
    $[83] = t14;
    $[84] = t18;
    $[85] = t19;
  } else {
    t19 = $[85];
  }
  let t20;
  if ($[86] !== t15 || $[87] !== t16 || $[88] !== t19) {
    t20 = <div className={t15}>{t16}{t19}</div>;
    $[86] = t15;
    $[87] = t16;
    $[88] = t19;
    $[89] = t20;
  } else {
    t20 = $[89];
  }
  return t20;
}
function _temp(edge) {
  return edge.node;
}